import React from 'react';
import {
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  FormGroup,
  Typography,
  Box,
  Button,
  IconButton,
  Stack,
  Divider,
  Grid
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';

const QuestionnaireSection = ({ section, responses, onChange, readOnly = false, compact = false, fieldErrors = {} }) => {
  const renderField = (item, itemResponses = responses, onItemChange = onChange) => {
    const fieldError = fieldErrors[item.linkId];
    const commonProps = {
      disabled: readOnly,
      size: compact ? "small" : "medium",
      fullWidth: true,
      required: Boolean(item.required),
      error: Boolean(fieldError),
      helperText: fieldError || undefined,
    };

    const spacing = compact ? { margin: "dense" } : { margin: "normal" };

    switch (item.type) {
      case 'string':
        return (
          <TextField
            {...commonProps}
            {...spacing}
            label={item.text}
            value={itemResponses[item.linkId] || ''}
            onChange={(e) => onItemChange(item.linkId, e.target.value)}
            variant={readOnly ? "filled" : "outlined"}
            sx={{ mb: compact ? 1 : 1.5 }}
          />
        );
      case 'choice':
        return (
          <Box sx={{ mb: compact ? 1 : 1.5 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>{item.text}</Typography>
            <RadioGroup
              value={itemResponses[item.linkId] || ''}
              onChange={(e) => onItemChange(item.linkId, e.target.value)}
              sx={{ ml: 1 }}
            >
              {item.answerOption?.map((option) => (
                <FormControlLabel
                  key={option.valueCoding.code}
                  value={option.valueCoding.code}
                  control={<Radio size={compact ? "small" : "medium"} />}
                  label={option.valueCoding.display}
                  sx={{ py: compact ? 0.1 : 0.25 }}
                />
              ))}
            </RadioGroup>
          </Box>
        );

      case 'boolean':
        return (
          <FormControlLabel
            control={
              <Checkbox
                checked={itemResponses[item.linkId] || false}
                onChange={(e) => onItemChange(item.linkId, e.target.checked)}
                size={compact ? "small" : "medium"}
              />
            }
            label={item.text}
            sx={{ py: compact ? 0.1 : 0.25, mb: compact ? 1 : 1.5 }}
          />
        );
      case 'text':
        return (
          <TextField
            {...commonProps}
            {...spacing}
            label={item.text}
            value={itemResponses[item.linkId] || ''}
            onChange={(e) => onItemChange(item.linkId, e.target.value)}
            multiline
            rows={compact ? 3 : 6} // Set the default number of rows here
            minRows={compact ? 2 : 3}
            maxRows={compact ? 4 : 6}
            sx={{ mb: compact ? 1 : 1.5 }}
          />
        );
      case 'date':
        return (
          <TextField
            {...commonProps}
            {...spacing}
            label={item.text}
            type="date"
            value={itemResponses[item.linkId] || ''}
            onChange={(e) => onItemChange(item.linkId, e.target.value)}
            InputLabelProps={{
              shrink: true,
            }}
            sx={{ mb: compact ? 1 : 1.5 }}
          />
        );
      case 'multiple-choice':
        // Special handling for Chronic Diseases to display in grid
        if (item.linkId === 'pmhChronicDiseases') {
          return (
            <Box sx={{ mb: compact ? 1.5 : 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>{item.text}</Typography>
              <Grid container spacing={1}>
                {item.answerOption?.map((option) => (
                  <Grid item xs={6} sm={4} md={3} key={option.valueCoding.code}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size={compact ? "small" : "medium"}
                          checked={Boolean((itemResponses[item.linkId] || []).includes(option.valueCoding.code))}
                          onChange={(e) => {
                            const currentValues = itemResponses[item.linkId] || [];
                            const newValues = e.target.checked
                              ? [...currentValues, option.valueCoding.code]
                              : currentValues.filter(v => v !== option.valueCoding.code);
                            onItemChange(item.linkId, newValues);
                          }}
                        />
                      }
                      label={option.valueCoding.display}
                      sx={{ 
                        py: compact ? 0.1 : 0.25,
                        '& .MuiFormControlLabel-label': {
                          fontSize: compact ? '0.85rem' : '1rem'
                        }
                      }}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          );
        }
        
        // Default multiple-choice rendering
        return (
          <FormGroup sx={{ mb: compact ? 1.5 : 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>{item.text}</Typography>
            {item.answerOption?.map((option) => (
              <FormControlLabel
                key={option.valueCoding.code}
                control={
                  <Checkbox
                    size={compact ? "small" : "medium"}
                    checked={Boolean((itemResponses[item.linkId] || []).includes(option.valueCoding.code))}
                    onChange={(e) => {
                      const currentValues = itemResponses[item.linkId] || [];
                      const newValues = e.target.checked
                        ? [...currentValues, option.valueCoding.code]
                        : currentValues.filter(v => v !== option.valueCoding.code);
                      onItemChange(item.linkId, newValues);
                    }}
                  />
                }
                label={option.valueCoding.display}
                sx={{ py: compact ? 0.1 : 0.25 }}
              />
            ))}
          </FormGroup>
        );

      default:
        return null;
    }
  };

  const renderSectionContent = (sectionItems, responseData, onResponseChange) => {
    return sectionItems?.map((item) => (
      <Box key={item.linkId} className="question-item">
        {renderField(item, responseData, onResponseChange)}
      </Box>
    ));
  };

  const renderRepeatingGroup = () => {
    const groupResponses = responses[section.linkId] || [{}];

    return (
      <Box>
        {groupResponses.map((groupResponse, index) => (
          <Stack 
            key={index} 
            sx={{ 
              p: compact ? 1.5 : 2, 
              mb: compact ? 1.5 : 2, 
              border: '1px solid #e0e0e0', 
              borderRadius: 1,
              position: 'relative',
              bgcolor: 'grey.25'
            }}
            spacing={compact ? 1 : 2}
          >
            {!readOnly && (
              <Box sx={{ position: 'absolute', right: 8, top: 8 }}>
                <IconButton
                  size="small"
                  onClick={() => {
                    const newResponses = [...groupResponses];
                    newResponses.splice(index, 1);
                    onChange(section.linkId, newResponses.length ? newResponses : [{}]);
                  }}
                  disabled={groupResponses.length === 1}
                >
                  <DeleteIcon fontSize={compact ? "small" : "medium"} />
                </IconButton>
              </Box>
            )}
            
            {renderSectionContent(
              section.item,
              groupResponse,
              (key, value) => {
                if (readOnly) return;
                const newResponses = [...groupResponses];
                newResponses[index] = { ...groupResponse, [key]: value };
                onChange(section.linkId, newResponses);
              }
            )}
          </Stack>
        ))}
        
        {!readOnly && (
          <Button
            startIcon={<AddCircleOutlineIcon />}
            onClick={() => onChange(section.linkId, [...groupResponses, {}])}
            size={compact ? "small" : "medium"}
            sx={{ mt: 1 }}
          >
            Add {section.text}
          </Button>
        )}
      </Box>
    );
  };

  const renderSimpleGroup = () => {
    return (
      <Box className="questionnaire-section">
        <Stack spacing={compact ? 1 : 1.5}>
          {renderSectionContent(section.item, responses, onChange)}
        </Stack>
      </Box>
    );
  };

  // Main render logic
  if (section.type === 'group') {
    return section.repeats ? renderRepeatingGroup() : renderSimpleGroup();
  }

  return renderSimpleGroup();
};

export default QuestionnaireSection;
